import React, { useEffect, useMemo, useState } from "react";

/* =========================================================
   ICONS
========================================================= */

const CalendarIcon = () => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="17" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const SearchIcon = () => (
  <svg
    width="25"
    height="25"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="7" />
    <line x1="16.5" y1="16.5" x2="22" y2="22" />
  </svg>
);

const UserIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.5-7 8-7s8 3 8 7" />
  </svg>
);

const ChevronDown = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ChevronLeft = () => (
  <svg
    width="25"
    height="25"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight = () => (
  <svg
    width="25"
    height="25"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);


/* =========================================================
   DATE HELPERS
========================================================= */

const pad = (value) =>
  String(value).padStart(2, "0");

const formatDateKey = (date) => {
  if (!date) return "";

  if (typeof date === "string") {
    return date;
  }

  return `${date.getFullYear()}-${pad(
    date.getMonth() + 1
  )}-${pad(date.getDate())}`;
};

const parseDateKey = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return value;
  }

  const [year, month, day] =
    String(value).split("-").map(Number);

  if (
    !year ||
    !month ||
    !day
  ) {
    return null;
  }

  return new Date(
    year,
    month - 1,
    day
  );
};

const startOfDay = (date) => {
  const result = new Date(date);

  result.setHours(
    0,
    0,
    0,
    0
  );

  return result;
};

const addDays = (
  date,
  amount
) => {
  const result = new Date(date);

  result.setDate(
    result.getDate() + amount
  );

  return result;
};

const addMonths = (
  date,
  amount
) => {
  const result = new Date(date);

  result.setMonth(
    result.getMonth() + amount
  );

  return result;
};

const isSameDay = (
  date1,
  date2
) => {
  if (!date1 || !date2) {
    return false;
  }

  return (
    date1.getFullYear() ===
      date2.getFullYear() &&
    date1.getMonth() ===
      date2.getMonth() &&
    date1.getDate() ===
      date2.getDate()
  );
};

const isBeforeDay = (
  date1,
  date2
) => {
  return (
    startOfDay(date1) <
    startOfDay(date2)
  );
};

const isBetween = (
  date,
  start,
  end
) => {
  if (!start || !end) {
    return false;
  }

  const current =
    startOfDay(date);

  const first =
    startOfDay(start);

  const last =
    startOfDay(end);

  return (
    current > first &&
    current < last
  );
};

const formatDisplayDate = (
  value
) => {
  const date =
    parseDateKey(value);

  if (!date) {
    return "Select date";
  }

  return date.toLocaleDateString(
    "en-US",
    {
      weekday: "short",
      day: "numeric",
      month: "short",
    }
  );
};

const getMonthLabel = (
  date
) => {
  return date.toLocaleDateString(
    "en-US",
    {
      month: "long",
      year: "numeric",
    }
  );
};


/* =========================================================
   CALENDAR MONTH
========================================================= */

const CalendarMonth = ({
  monthDate,
  checkIn,
  checkOut,
  onSelectDate,
  today,
}) => {
  const firstDay =
    new Date(
      monthDate.getFullYear(),
      monthDate.getMonth(),
      1
    );

  const daysInMonth =
    new Date(
      monthDate.getFullYear(),
      monthDate.getMonth() + 1,
      0
    ).getDate();

  // Monday = 0
  // Sunday = 6
  const mondayIndex =
    (firstDay.getDay() + 6) %
    7;

  const cells = [];

  for (
    let i = 0;
    i < mondayIndex;
    i++
  ) {
    cells.push(null);
  }

  for (
    let day = 1;
    day <= daysInMonth;
    day++
  ) {
    cells.push(
      new Date(
        monthDate.getFullYear(),
        monthDate.getMonth(),
        day
      )
    );
  }

  while (
    cells.length % 7 !== 0
  ) {
    cells.push(null);
  }

  return (
    <div className="be-calendar-month">

      <div className="be-calendar-month-title">
        {getMonthLabel(monthDate)}
      </div>

      <div className="be-calendar-weekdays">
        {[
          "M",
          "T",
          "W",
          "T",
          "F",
          "S",
          "S",
        ].map(
          (day, index) => (
            <div key={index}>
              {day}
            </div>
          )
        )}
      </div>

      <div className="be-calendar-grid">

        {cells.map(
          (date, index) => {

            if (!date) {
              return (
                <div
                  key={`empty-${index}`}
                  className="be-calendar-day empty"
                />
              );
            }

            const dateKey =
              formatDateKey(date);

            const selectedStart =
              isSameDay(
                date,
                checkIn
              );

            const selectedEnd =
              isSameDay(
                date,
                checkOut
              );

            const range =
              isBetween(
                date,
                checkIn,
                checkOut
              );

            const isPast =
              isBeforeDay(
                date,
                today
              );

            const isToday =
              isSameDay(
                date,
                today
              );

            return (
              <button
                key={dateKey}
                type="button"
                disabled={isPast}
                onClick={() =>
                  onSelectDate(
                    date
                  )
                }
                className={[
                  "be-calendar-day",
                  selectedStart
                    ? "selected-start"
                    : "",
                  selectedEnd
                    ? "selected-end"
                    : "",
                  range
                    ? "in-range"
                    : "",
                  isPast
                    ? "past"
                    : "",
                  isToday
                    ? "today"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <span>
                  {date.getDate()}
                </span>
              </button>
            );
          }
        )}

      </div>
    </div>
  );
};


/* =========================================================
   BOOKING CALENDAR
========================================================= */

const BookingCalendar = ({
  activeDate,
  checkIn,
  checkOut,
  onSelectDate,
  onClose,
}) => {

  const parsedCheckIn =
    parseDateKey(checkIn);

  const parsedCheckOut =
    parseDateKey(checkOut);

  const selectedDate =
    activeDate === "checkOut"
      ? parsedCheckOut
      : parsedCheckIn;

  const initialMonth =
    selectedDate
      ? new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          1
        )
      : new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          1
        );

  const [
    visibleMonth,
    setVisibleMonth,
  ] = useState(
    initialMonth
  );

  const [
    flexibleDates,
    setFlexibleDates,
  ] = useState(false);

  const today = useMemo(
    () =>
      startOfDay(
        new Date()
      ),
    []
  );

  useEffect(() => {

    const date =
      activeDate === "checkOut"
        ? parsedCheckOut
        : parsedCheckIn;

    if (date) {
      setVisibleMonth(
        new Date(
          date.getFullYear(),
          date.getMonth(),
          1
        )
      );
    }

  }, [
    activeDate,
    checkIn,
    checkOut,
  ]);

  const secondMonth =
    addMonths(
      visibleMonth,
      1
    );

  const nights =
    parsedCheckIn &&
    parsedCheckOut
      ? Math.round(
          (
            parsedCheckOut.getTime() -
            parsedCheckIn.getTime()
          ) /
            (1000 *
              60 *
              60 *
              24)
        )
      : 0;

  return (
    <div className="be-calendar-popover">

      {/* HEADER */}

      <div className="be-calendar-topbar">

        <div className="be-date-mode">

          <button
            type="button"
            className={
              activeDate ===
              "checkIn"
                ? "active"
                : ""
            }
            onClick={() => {
              // Search 2 controls activeDate
              // from the parent.
            }}
          >
            Check-in
          </button>

          <button
            type="button"
            className={
              activeDate ===
              "checkOut"
                ? "active"
                : ""
            }
            onClick={() => {
              if (parsedCheckIn) {
                onSelectDate(
                  parsedCheckIn
                );
              }
            }}
          >
            Check-out
          </button>

        </div>

        <label className="be-flexible-toggle">

          <input
            type="checkbox"
            checked={
              flexibleDates
            }
            onChange={(e) =>
              setFlexibleDates(
                e.target.checked
              )
            }
          />

          <span className="be-toggle-track">
            <span />
          </span>

          <span>
            Flexible dates
          </span>

        </label>

      </div>


      {/* MONTH NAVIGATION */}

      <div className="be-calendar-navigation">

        <button
          type="button"
          className="be-calendar-arrow"
          onClick={() =>
            setVisibleMonth(
              addMonths(
                visibleMonth,
                -1
              )
            )
          }
        >
          <ChevronLeft />
        </button>

        <div className="be-month-select">

          <span>
            {getMonthLabel(
              visibleMonth
            )}
          </span>

          <ChevronDown />

        </div>

        <div className="be-month-select">

          <span>
            {getMonthLabel(
              secondMonth
            )}
          </span>

          <ChevronDown />

        </div>

        <button
          type="button"
          className="be-calendar-arrow"
          onClick={() =>
            setVisibleMonth(
              addMonths(
                visibleMonth,
                1
              )
            )
          }
        >
          <ChevronRight />
        </button>

      </div>


      {/* CALENDAR MONTHS */}

      <div className="be-calendar-months">

        <CalendarMonth
          monthDate={
            visibleMonth
          }
          checkIn={
            parsedCheckIn
          }
          checkOut={
            parsedCheckOut
          }
          onSelectDate={
            onSelectDate
          }
          today={today}
        />

        <CalendarMonth
          monthDate={
            secondMonth
          }
          checkIn={
            parsedCheckIn
          }
          checkOut={
            parsedCheckOut
          }
          onSelectDate={
            onSelectDate
          }
          today={today}
        />

      </div>


      {/* FOOTER */}

      <div className="be-calendar-footer">

        {parsedCheckIn &&
        parsedCheckOut ? (

          <strong>
            From{" "}
            {formatDisplayDate(
              formatDateKey(
                parsedCheckIn
              )
            )}{" "}
            to{" "}
            {formatDisplayDate(
              formatDateKey(
                parsedCheckOut
              )
            )}{" "}
            ({nights}-night)
          </strong>

        ) : parsedCheckIn ? (

          <strong>
            Select your check-out date
          </strong>

        ) : (

          <strong>
            Select your check-in date
          </strong>

        )}

      </div>

    </div>
  );
};


/* =========================================================
   COUNTER
========================================================= */

const Counter = ({
  value,
  min,
  onDecrease,
  onIncrease,
}) => (
  <div className="be-counter">

    <button
      type="button"
      disabled={
        value <= min
      }
      onClick={
        onDecrease
      }
      className="be-counter-button"
    >
      −
    </button>

    <span className="be-counter-value">
      {value}
    </span>

    <button
      type="button"
      onClick={
        onIncrease
      }
      className="be-counter-button"
    >
      +
    </button>

  </div>
);


/* =========================================================
   GUEST POPUP
========================================================= */

const GuestPopover = ({
  rooms,
  setRooms,
  adults,
  setAdults,
  children,
  setChildren,
  onClose,
}) => {

  return (
    <div className="be-guest-popover">

      {/* ROOMS */}

      <div className="be-guest-header">

        <strong>
          Rooms
        </strong>

        <Counter
          value={rooms}
          min={1}
          onDecrease={() =>
            setRooms(
              Math.max(
                1,
                rooms - 1
              )
            )
          }
          onIncrease={() =>
            setRooms(
              rooms + 1
            )
          }
        />

      </div>


      {/* ROOM LIST */}

      {Array.from(
        {
          length: rooms,
        },
        (_, index) => (
          <div
            key={index}
            className="be-room-title"
          >
            Room {index + 1}
          </div>
        )
      )}


      {/* ADULTS */}

      <div className="be-guest-row">

        <div className="be-guest-row-label">

          <strong>
            Nº of adults
          </strong>

        </div>

        <Counter
          value={adults}
          min={1}
          onDecrease={() =>
            setAdults(
              Math.max(
                1,
                adults - 1
              )
            )
          }
          onIncrease={() =>
            setAdults(
              adults + 1
            )
          }
        />

      </div>


      {/* CHILDREN */}

      <div className="be-guest-row">

        <div className="be-guest-row-label">

          <strong>
            Nº of children
          </strong>

          <span>
            (Up to 12 years old)
          </span>

        </div>

        <Counter
          value={children}
          min={0}
          onDecrease={() =>
            setChildren(
              Math.max(
                0,
                children - 1
              )
            )
          }
          onIncrease={() =>
            setChildren(
              children + 1
            )
          }
        />

      </div>


      {/* FOOTER */}

      <div className="be-guest-footer">

        <button
          type="button"
          className="be-add-room"
          onClick={() =>
            setRooms(
              rooms + 1
            )
          }
        >
          ADD NEW ROOM
        </button>

        <span className="be-info-icon">
          i
        </span>

        <button
          type="button"
          className="be-done-button"
          onClick={
            onClose
          }
        >
          DONE
        </button>

      </div>

    </div>
  );
};


/* =========================================================
   MAIN BOOKING SEARCH
========================================================= */

const BookingSearch = ({
  searchDetails,
  setSearchDetails,
  handleSearch,
  loadingRooms,
  searchingAvailability,
  property,
}) => {

  console.log("🟢 BOOKING SEARCH RENDER:", searchDetails);

  console.log("🏨 BOOKING SEARCH PROPERTY:", property);

  useEffect(() => {
    console.log(
      "🟡 BOOKING SEARCH RECEIVED STATE:",
      searchDetails
    );

    console.log(
      "🏨 PROPERTY DATA RECEIVED:",
      property
    );
  }, [
    searchDetails,
    property,
  ]);


  /* =======================================================
     RESOLVE PROPERTY INFORMATION
  ======================================================= */

  const propertyInfo = useMemo(() => {

    if (!property) {
      return {};
    }

    const source =
      property?.data?.property ||
      property?.property ||
      property?.data ||
      property;

    return source || {};

  }, [property]);


  /* =======================================================
     PROPERTY ADDRESS
  ======================================================= */

  const propertyAddress = useMemo(() => {

    const address =
      propertyInfo?.address ||
      propertyInfo?.addressLine ||
      propertyInfo?.addressLine1 ||
      propertyInfo?.fullAddress ||
      propertyInfo?.location?.address ||
      propertyInfo?.location?.fullAddress ||
      propertyInfo?.propertyAddress ||
      "";

    return String(address).trim();

  }, [propertyInfo]);


  /* =======================================================
     PROPERTY NAME
  ======================================================= */

  const propertyName =
    propertyInfo?.propertyName ||
    propertyInfo?.name ||
    propertyInfo?.hotelName ||
    propertyInfo?.hotel?.name ||
    "";

  const [
    calendarOpen,
    setCalendarOpen,
  ] = useState(false);

  const [
    activeDate,
    setActiveDate,
  ] = useState("checkIn");

  const [
    guestOpen,
    setGuestOpen,
  ] = useState(false);


  /* =======================================================
     READ SEARCH VALUES
  ======================================================= */

  const checkIn =
    searchDetails?.checkIn || "";

  const checkOut =
    searchDetails?.checkOut || "";

  const rooms =
    Number(
      searchDetails?.rooms
    ) || 1;

  const adults =
    Number(
      searchDetails?.adults
    ) || 1;

  const children =
    Number(
      searchDetails?.children
    ) || 0;


  /* =======================================================
     UPDATE SEARCH DETAILS
  ======================================================= */

  const updateSearchDetails = (
    updates
  ) => {

    setSearchDetails(
      (previous) => ({
        ...previous,
        ...updates,
      })
    );
  };


  /* =======================================================
     DATE PICKER
  ======================================================= */

  const openCalendar = (
    field
  ) => {

    setGuestOpen(false);

    setActiveDate(field);

    setCalendarOpen(true);
  };


  /* =======================================================
     DATE SELECTION
  ======================================================= */

  const handleSelectDate = (
    date
  ) => {

    if (!date) {
      return;
    }

    const selectedKey =
      formatDateKey(date);

    console.log(
      "📅 SELECTED DATE:",
      selectedKey,
      "FIELD:",
      activeDate
    );


    /* -----------------------------------------------------
       CHECK-IN
    ----------------------------------------------------- */

    if (
      activeDate ===
      "checkIn"
    ) {

      let nextCheckOut =
        checkOut;

      const existingCheckout =
        parseDateKey(
          checkOut
        );

      /*
       * If checkout is before/equal
       * to new check-in,
       * automatically move checkout
       * to next day.
       */

      if (
        existingCheckout &&
        !isBeforeDay(
          date,
          existingCheckout
        )
      ) {

        nextCheckOut =
          formatDateKey(
            addDays(
              date,
              1
            )
          );
      }

      updateSearchDetails({
        checkIn:
          selectedKey,

        checkOut:
          nextCheckOut,
      });

      /*
       * Automatically move
       * to checkout.
       */

      setActiveDate(
        "checkOut"
      );

      return;
    }


    /* -----------------------------------------------------
       CHECK-OUT
    ----------------------------------------------------- */

    if (
      activeDate ===
      "checkOut"
    ) {

      const existingCheckIn =
        parseDateKey(
          checkIn
        );

      /*
       * Checkout must be after
       * check-in.
       */

      if (
        existingCheckIn &&
        !isBeforeDay(
          existingCheckIn,
          date
        )
      ) {

        const nextDay =
          addDays(
            existingCheckIn,
            1
          );

        updateSearchDetails({
          checkOut:
            formatDateKey(
              nextDay
            ),
        });

        return;
      }

      updateSearchDetails({
        checkOut:
          selectedKey,
      });

      /*
       * Close calendar after
       * successful checkout.
       */

      setCalendarOpen(false);
    }
  };


  /* =======================================================
     GUEST UPDATERS
     
     These are intentionally connected to
     searchDetails instead of local state.
  ======================================================= */

  const setRooms = (
    value
  ) => {

    const nextValue =
      typeof value ===
      "function"
        ? value(rooms)
        : value;

    updateSearchDetails({
      rooms:
        Math.max(
          1,
          Number(
            nextValue
          ) || 1
        ),
    });
  };


  const setAdults = (
    value
  ) => {

    const nextValue =
      typeof value ===
      "function"
        ? value(adults)
        : value;

    updateSearchDetails({
      adults:
        Math.max(
          1,
          Number(
            nextValue
          ) || 1
        ),
    });
  };


  const setChildren = (
    value
  ) => {

    const nextValue =
      typeof value ===
      "function"
        ? value(children)
        : value;

    updateSearchDetails({
      children:
        Math.max(
          0,
          Number(
            nextValue
          ) || 0
        ),
    });
  };


  /* =======================================================
     GUEST SUMMARY
  ======================================================= */

  const guestSummary =
    `${rooms} ${
      rooms === 1
        ? "room"
        : "rooms"
    }, ${adults} ${
      adults === 1
        ? "adult"
        : "adults"
    }${
      children > 0
        ? `, ${children} ${
            children === 1
              ? "child"
              : "children"
          }`
        : ""
    }`;


  /* =======================================================
     SEARCH
     
     IMPORTANT:
     handleSearch receives the parent state.
     We do not create another copy here.
  ======================================================= */

  const handleSearchClick =
    () => {

      console.log(
        "🚀 FINAL BOOKING SEARCH:",
        {
          ...searchDetails,
        }
      );

      setCalendarOpen(
        false
      );

      setGuestOpen(
        false
      );

      /*
       * Use the existing parent's
       * search function.
       */

      handleSearch();
    };


  /* =======================================================
     NIGHTS
  ======================================================= */

  const nights =
    useMemo(() => {

      if (
        !checkIn ||
        !checkOut
      ) {
        return 0;
      }

      const start =
        parseDateKey(
          checkIn
        );

      const end =
        parseDateKey(
          checkOut
        );

      if (
        !start ||
        !end
      ) {
        return 0;
      }

      const difference =
        end.getTime() -
        start.getTime();

      const calculated =
        Math.round(
          difference /
            (1000 *
              60 *
              60 *
              24)
        );

      return calculated >
        0
        ? calculated
        : 0;

    }, [
      checkIn,
      checkOut,
    ]);


  /* =======================================================
     RENDER
  ======================================================= */

  return (
 <section className="be-search-wrapper be-booking-sticky-container">
      <div className="be-search-box">
        {/* =================================================
            HOTEL ADDRESS
        ================================================= */}
        <div className="be-search-field be-destination">
          <span className="be-search-label">
            You're looking for
          </span>
          <div className="be-search-value be-hotel-address">
            <span className="be-search-icon">

              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z" />

                <circle
                  cx="12"
                  cy="10"
                  r="2.5"
                />
              </svg>

            </span>

            <span
              title={
                property?.address ||
                property?.propertyName
              }
            >
              {property?.address ||
                property?.propertyName ||
                "Hotel address"}
            </span>

          </div>

        </div>


        {/* =================================================
            CHECK-IN
        ================================================= */}

        <button
          type="button"
          className={[
            "be-search-field",
            "be-date-field",
            calendarOpen &&
            activeDate ===
              "checkIn"
              ? "active"
              : "",
          ].join(" ")}
          onClick={() =>
            openCalendar(
              "checkIn"
            )
          }
        >

          <span className="be-search-label">
            Check-in
          </span>

          <div className="be-search-value">

            <span className="be-search-icon">
              <CalendarIcon />
            </span>

            <span className="be-date-text">

              <strong>
                {formatDisplayDate(
                  checkIn
                )}
              </strong>

            </span>

            <span className="be-field-chevron">
              <ChevronDown />
            </span>

          </div>

        </button>


        {/* =================================================
            CHECK-OUT
        ================================================= */}

        <button
          type="button"
          className={[
            "be-search-field",
            "be-date-field",
            calendarOpen &&
            activeDate ===
              "checkOut"
              ? "active"
              : "",
          ].join(" ")}
          onClick={() =>
            openCalendar(
              "checkOut"
            )
          }
        >

          <span className="be-search-label">
            Check-out
          </span>

          <div className="be-search-value">

            <span className="be-search-icon">
              <CalendarIcon />
            </span>

            <span className="be-date-text">

              <strong>
                {formatDisplayDate(
                  checkOut
                )}
              </strong>

            </span>

            <span className="be-field-chevron">
              <ChevronDown />
            </span>

          </div>

        </button>


        {/* =================================================
            ROOMS & GUESTS
        ================================================= */}

        <button
          type="button"
          className={[
            "be-search-field",
            "be-guests",
            guestOpen
              ? "active"
              : "",
          ].join(" ")}
          onClick={() => {

            setCalendarOpen(
              false
            );

            setGuestOpen(
              !guestOpen
            );

          }}
        >

          <span className="be-search-label">
            Rooms & Guests
          </span>

          <div className="be-search-value">

            <span className="be-search-icon">
              <UserIcon />
            </span>

            <span className="be-guest-summary">
              {guestSummary}
            </span>

            <span className="be-field-chevron">
              <ChevronDown />
            </span>

          </div>

        </button>


        {/* =================================================
            SEARCH
        ================================================= */}

        <button
          type="button"
          className="be-search-button"
          onClick={
            handleSearchClick
          }
          disabled={
            loadingRooms ||
            searchingAvailability
          }
        >

          <SearchIcon />

          <span>
            {loadingRooms
              ? "Loading..."
              : searchingAvailability
              ? "Checking..."
              : "SEARCH"}
          </span>

        </button>

      </div>


      {/* =================================================
          CALENDAR POPOVER
      ================================================= */}

      {calendarOpen && (
        <BookingCalendar
          activeDate={
            activeDate
          }
          checkIn={
            checkIn
          }
          checkOut={
            checkOut
          }
          onSelectDate={
            handleSelectDate
          }
          onClose={() =>
            setCalendarOpen(
              false
            )
          }
        />
      )}


      {/* =================================================
          GUEST POPOVER
      ================================================= */}

      {guestOpen && (
        <GuestPopover
          rooms={
            rooms
          }
          setRooms={
            setRooms
          }
          adults={
            adults
          }
          setAdults={
            setAdults
          }
          children={
            children
          }
          setChildren={
            setChildren
          }
          onClose={() =>
            setGuestOpen(
              false
            )
          }
        />
      )}

    </section>
  );
};

export default BookingSearch;